import { extent, ScaleQuantile, scaleQuantile, ScaleQuantize, scaleQuantize, scaleThreshold, ScaleThreshold } from "d3"
import {comparer, observable, reaction} from "mobx"
import {
  addDisposer, getEnv, getSnapshot, hasEnv, IAnyStateTreeNode, Instance, ISerializedActionCall,
  resolveIdentifier, SnapshotIn, types
} from "mobx-state-tree"
import {AttributeType, attributeTypes} from "../../../models/data/attribute-types"
import {DataSet, IDataSet} from "../../../models/data/data-set"
import {isAddCasesAction, isCaseValueChangeAction} from "../../../models/data/data-set-actions"
import {ICase} from "../../../models/data/data-set-types"
import {idOfChildmostCollectionForAttributes} from "../../../models/data/data-set-utils"
import {FilteredCases, IFilteredChangedCases} from "../../../models/data/filtered-cases"
import {Formula, IFormula} from "../../../models/formula/formula"
import {applyModelChange} from "../../../models/history/apply-model-change"
import {IDataSetMetadata, DataSetMetadata} from "../../../models/shared/data-set-metadata"
import { getMetadataFromDataSet } from "../../../models/shared/shared-data-utils"
import {
  kDefaultHighAttributeColor, kDefaultLowAttributeColor
} from "../../../models/shared/data-set-metadata-constants"
import {getChoroplethColors, missingColor, parseColor} from "../../../utilities/color-utils"
import { numericSortComparator } from "../../../utilities/data-utils"
import { stringValuesToDateSeconds } from "../../../utilities/date-utils"
import {hashStringSets, typedId, uniqueId} from "../../../utilities/js-utils"
import { equalFrequencyBins, isFiniteNumber } from "../../../utilities/math-utils"
import { kDefaultPointShape, PointShape } from "../../../utilities/point-shape-utils"
import { cachedFnWithArgsFactory, onAnyAction } from "../../../utilities/mst-utils"
import { AxisPlace } from "../../axis/axis-types"
import {GraphPlace} from "../../axis-graph-shared"
import { getScaleThresholds } from "../components/legend/choropleth-legend/choropleth-legend"
import {CaseData} from "../d3-types"
import {
  AttrRole, GraphAttrRole, TipAttrRoles, graphPlaceToAttrRole, kOther, kMain, GraphSplitAttrRoles
} from "../data-display-types"
import { dataDisplayGetNumericValue } from "../data-display-value-utils"

// Default number of bins/colors for a numeric legend. The inspector control treats an override
// equal to this default as "no override" and clears it rather than storing it redundantly; the
// metadata layer itself does not special-case the default, so a value of 5 set directly (e.g. via
// a patch/import/plugin) is stored as-is.
export const kDefaultLegendBinCount = 5

export const AttributeDescription = types
  .model('AttributeDescription', {
    attributeID: types.string,
    // user-specified type, e.g. treat as numeric
    type: types.maybe(types.enumeration([...attributeTypes]))
  })
  .actions(self => ({
    setType(type: AttributeType) {
      self.type = type
    }
  }))

export type RoleAttrIDPair = { role: AttrRole, attributeID: string }

export interface IAttributeDescriptionSnapshot extends SnapshotIn<typeof AttributeDescription> {
}

export type GraphAttributeDescriptionsMapSnapshot = Partial<Record<GraphAttrRole, IAttributeDescriptionSnapshot>>
export type AttributeDescriptionsMapSnapshot = Partial<Record<AttrRole, IAttributeDescriptionSnapshot>>

export const kUnknownDataConfigurationType = "unknownDataConfigurationType"
export const kDataConfigurationType = "dataConfigurationType"

// A DataConfigurationModel (or a containing tile model) can be created with an environment containing
// a provisional dataset and metadata. In this case, the provisional dataset or metadata will be retrieved
// instead of the DataConfiguration's own. This allows the DI system to set up a graph tile snapshot,
// referencing the dataset and metadata as necessary, outside the main MST tree.
interface IProvisionalEnvironment {
  provisionalDataSet?: IDataSet
  provisionalMetadata?: IDataSetMetadata
}

export function getProvisionalDataSet(node: IAnyStateTreeNode | null) {
  const env = node && hasEnv(node) ? getEnv<IProvisionalEnvironment>(node) : {}
  return env.provisionalDataSet
}

export function getProvisionalMetadata(node: IAnyStateTreeNode | null) {
  const env = node && hasEnv(node) ? getEnv<IProvisionalEnvironment>(node) : {}
  return env.provisionalMetadata
}

export const DataConfigurationModel = types
  .model('DataConfigurationModel', {
    id: types.optional(types.identifier, () => typedId("DCON")),
    type: types.optional(types.string, kDataConfigurationType),
    // keys are AttrRoles, excluding y role
    _attributeDescriptions: types.map(AttributeDescription),
    dataset: types.safeReference(DataSet, {
      get(identifier: string, parent: IAnyStateTreeNode | null): any {
        return getProvisionalDataSet(parent) ?? resolveIdentifier<typeof DataSet>(DataSet, parent, identifier)
      },
      set(dataSet: IDataSet) {
        return dataSet.id
      }
    }),
    metadata: types.safeReference(DataSetMetadata, {
      get(identifier: string, parent: IAnyStateTreeNode | null): any {
        return getProvisionalMetadata(parent) ??
          resolveIdentifier<typeof DataSetMetadata>(DataSetMetadata, parent, identifier)
      },
      set(metadata: IDataSetMetadata) {
        return metadata.id
      }
    }),
    hiddenCases: types.array(types.string),
    displayOnlySelectedCases: types.maybe(types.boolean),
    filterFormula: types.maybe(Formula),
    legendQuantilesAreLocked: types.maybe(types.boolean),
    legendQuantiles: types.array(types.number)
  })
  .volatile(() => ({
    actionHandlerDisposer: undefined as (() => void) | undefined,
    filteredCases: observable.array<FilteredCases>([], { deep: false }),
    handlers: new Map<string, (actionCall: ISerializedActionCall) => void>(),
    pointsNeedUpdating: false,
    casesChangeCount: 0,
    // cached result of filter formula evaluation for each case ID
    filteredOutCaseIds: observable.set<string>(),
    filterFormulaError: "",
    // When true, suppress the next animation triggered by case changes (e.g., filter formula recalculation)
    suppressAnimation: false,
    // The following is set in useSubAxis:setupCategories based on how many fit in available space
    numberOfCategoriesLimitByRole: observable.map<AttrRole, Maybe<number>>(),
    // When legendQuantilesAreLocked is true, this holds the quantile thresholds
    _legendNumericColorScale: null as ScaleQuantize<string, never> | ScaleQuantile<string, never> |
      ScaleThreshold<number, string, never> | null,
  }))
  .views(self => ({
    get axisAttributeIDs() {
      // Note that 'caption' and 'legend' are not roles we include here
      return (['x', 'y', 'rightNumeric', 'topSplit', 'rightSplit', 'lat', 'long', 'polygon'] as const)
        .map(aRole => this.attributeID(aRole))
        .filter(id => !!id)
    },
    get childmostCollectionIDForAxisAttributes() {
        return idOfChildmostCollectionForAttributes(this.axisAttributeIDs, self.dataset)
    },
    get isEmpty() {
      return self._attributeDescriptions.size === 0
    },
    get attributeDescriptions() {
      return getSnapshot(self._attributeDescriptions) as AttributeDescriptionsMapSnapshot
    },
    get attributeDescriptionsStr() {
      return JSON.stringify(this.attributeDescriptions)
    },
    isAttributeAllowedForNonAxisRole(attrID?: string) {
      if (!attrID) return false
      // The legend and caption attributes are not allowed to be an attribute belonging
      // to a collection that is a child of the childmost collection (using axis attributes).
      const childmostCollectionID = this.childmostCollectionIDForAxisAttributes,
        collections = self.dataset?.collections || []
      let allowed = false
      for (const collection of collections) {
        allowed = !!collection.getAttribute(attrID)
        if (allowed || collection.id === childmostCollectionID) break
      }
      return allowed
    },
    attributeDescriptionForRole(role: AttrRole) {
      const attrDesc = this.attributeDescriptions[role]
      return (role !== "legend" && role !== "caption") || this.isAttributeAllowedForNonAxisRole(attrDesc?.attributeID)
        ? attrDesc : undefined
    },
    // returns empty string (rather than undefined) for roles without attributes
    attributeID(role: AttrRole) {
      const defaultCaptionAttributeID = () => {
        // We find the childmost collection and return the first non-hidden attribute in that collection. If there is
        // no childmost collection, we return the first non-hidden attribute in the dataset.
        const metadata = getMetadataFromDataSet(self.dataset)
        const attrIDs = (['x', 'y', 'rightNumeric', 'topSplit', 'rightSplit', 'legend',
              'lat', 'long', 'polygon'] as const)
            .map(aRole => this.attributeID(aRole))
            .filter(id => !!id),
          childmostCollectionID = idOfChildmostCollectionForAttributes(attrIDs, self.dataset)
        if (childmostCollectionID) {
          const childmostCollection = self.dataset?.getCollection(childmostCollectionID),
            childmostCollectionAttributes =
              childmostCollection?.attributes.filter(attr => attr?.id && !metadata?.isHidden(attr?.id))
          if (childmostCollectionAttributes?.length) {
            const firstAttribute = childmostCollectionAttributes[0]
            return firstAttribute?.id
          }
        }
        return self.dataset?.childCollection.attributes[0]?.id
      }

      let attrID = this.attributeDescriptionForRole(role)?.attributeID || ""
      if ((role === "caption") && !attrID) {
        attrID = defaultCaptionAttributeID() || ""
      }
      return attrID
    },
    attributeType(role: AttrRole) {
      const desc = this.attributeDescriptionForRole(role)
      if (desc?.type) {
        return desc.type
      }
      const attrID = this.attributeID(role)
      const attr = attrID ? self.dataset?.attrFromID(attrID) : undefined
      return attr?.type
    },
    roleForAttributeWithCategoryLimit(attrID: string) {
      return GraphSplitAttrRoles.find(role => {
        return self.numberOfCategoriesLimitByRole.get(role) !== undefined
      })
    },
    get places() {
      const places = new Set<string>(Object.keys(this.attributeDescriptions))
      self.dataset?.attributes.length && places.add("caption")
      return Array.from(places) as AttrRole[]
    },
    rolesForAttribute(attrID: string) {
      const roles: AttrRole[] = []
      self._attributeDescriptions.forEach((desc, role) => {
        if (desc?.attributeID === attrID) {
          roles.push(role as AttrRole)
        }
      })
      return roles
    },
    get hiddenCasesSet() {
      return new Set(self.hiddenCases)
    },
  }))
  .actions(self => ({
    clearFilteredCases() {
      self.filteredCases.forEach(aFilteredCases => aFilteredCases.destroy())
      self.filteredCases.clear()
    },
    beforeDestroy() {
      this.clearFilteredCases()

      self.actionHandlerDisposer?.()
    },
    _setAttributeDescription(iRole: AttrRole, iDesc?: IAttributeDescriptionSnapshot) {
      if (iDesc?.attributeID) {
        self._attributeDescriptions.set(iRole, iDesc)
      } else {
        self._attributeDescriptions.delete(iRole)
      }
    },
    setPointsNeedUpdating(needUpdating: boolean) {
      self.pointsNeedUpdating = needUpdating
    }
  }))
  .views(self => ({
    /*
     * The legend attribute the user assigned, whether or not this display can honor it.
     *
     * Read off the description rather than through `attributeID`, which answers differently by
     * display: the base filters an unusable legend out, while GraphDataConfigurationModel's
     * override does not. The assignment is the user's intent either way, and the legend has to be
     * able to name it -- and offer to remove it -- rather than drop it silently.
     */
    get assignedLegendAttributeID(): string {
      return self._attributeDescriptions.get("legend")?.attributeID ?? ""
    },
    /*
     * Whether that attribute is one this display cannot color or shape points by, because it lives
     * in a collection more childmost than the plotted cases -- a point then stands for several of
     * its values at once, which is why those points draw in the missing-value color.
     */
    get legendAttributeIsInoperable(): boolean {
      const attrID = this.assignedLegendAttributeID
      /*
       * Deleting an attribute leaves its ID behind in the description -- see the todo in
       * getLegendColorForCase -- and no collection holds it, so the allowed-check below says no.
       * Without this the legend would explain itself over a name that is no longer there.
       */
      if (!attrID || !self.dataset?.getAttribute(attrID)) return false

      return !self.isAttributeAllowedForNonAxisRole(attrID)
    },
    _caseHasValidValuesForDescriptions(data: IDataSet, caseID: string,
                                       descriptions: AttributeDescriptionsMapSnapshot) {
      return Object.entries(descriptions).every(([role, {attributeID}]) => {
        // can still plot the case without a caption or a legend
        if (["caption", "legend"].includes(role)) return true
        switch (self.attributeType(role as AttrRole)) {
          // TODO: handle "date" type
          case "numeric":
            return isFiniteNumber(dataDisplayGetNumericValue(data, caseID, attributeID, true))
          case "categorical":
            // Treat 0 as a string and return true for all non-empty strings
            return !!data.getStrValue(caseID, attributeID)
          default: {
            // for now, all other types must just be non-empty
            const value = data.getValue(caseID, attributeID)
            return value != null && value !== ""
          }
        }
      })
    },
    // This function can be called either here in this base class or in a subclass to handle the situation in which
    // caseArrayNumber === 0.
    _filterCase(data: IDataSet, caseID: string) {
      // If the case is hidden or filtered out we don't plot it.
      // Also, if displayOnlySelectedCases is true, we only plot selected cases.
      if (self.hiddenCasesSet.has(caseID) || self.filteredOutCaseIds.has(caseID) ||
        (self.displayOnlySelectedCases && self.dataset && !self.dataset.isCaseSelected(caseID))) {
        return false
      }
      return this._caseHasValidValuesForDescriptions(data, caseID, self.attributeDescriptions)
    },
  }))
  .views(self => ({
    filterCase(data: IDataSet, caseID: string, caseArrayNumber?: number) {
      return self._filterCase(data, caseID)
    }
  }))
  .views(self => ({
    get hasFilterFormula() {
      return !!self.filterFormula && !self.filterFormula.empty
    },
    get attributes() {
      return self.places.map(place => self.attributeID(place)).filter(attrID => !!attrID)
    },
    get uniqueAttributes() {
      return Array.from(new Set<string>(this.attributes))
    },
    get tipAttributes(): RoleAttrIDPair[] {
      return TipAttrRoles
        .map(role => {
          return {role, attributeID: self.attributeID(role) || ''}
        })
        .filter(pair => {
          return self.attributeType(pair.role) !== 'qualitative' &&
            ((pair.role !== 'legend' && pair.role !== 'caption') ||
            self.isAttributeAllowedForNonAxisRole(pair.attributeID)) && !!pair.attributeID
        })
    },
    get uniqueTipAttributes() {
      const tipAttributes = this.tipAttributes,
        idCounts: Record<string, number> = {}
      tipAttributes.forEach((aPair: RoleAttrIDPair) => {
        idCounts[aPair.attributeID] = (idCounts[aPair.attributeID] || 0) + 1
      })
      return tipAttributes.filter((aPair: RoleAttrIDPair) => {
        if (idCounts[aPair.attributeID] > 1) {
          idCounts[aPair.attributeID]--
          return false
        }
        return true
      })
    },
    get noAttributesAssigned() {
      // The first attribute is always assigned as 'caption'. So it's really no attributes assigned except for that
      return this.attributes.length <= 1
    },
    /**
     * Return all cases that are plotted at least once:
     * - set aside cases in the dataset should be excluded
     * - cases filtered by the filter formula in the dataset should be excluded
     * - cases hidden in the visualization should be excluded
     * - cases filtered by the filter formula in the visualization should be excluded
     * - cases that do not have valid values for the configured role attribute descriptions. An example is
     *   when a case has a non-numeric value for a numeric axis. If the case can be shown multiple times,
     *   all instances have to be invalid for it to be excluded.
     */
    get visibleCaseIds() {
      const allCaseIds = new Set<string>()
      self.filteredCases.forEach(aFilteredCases => {
        if (aFilteredCases) {
          aFilteredCases.caseIds.forEach(id => allCaseIds.add(id))
        }
      })
      return allCaseIds
    },
    /**
     * Note that in order to eliminate a selected case from the graph's selection, we have to check that it is not
     * present in any of the case sets, not just the 0th one.
     */
    get selection() {
      if (!self.dataset) return []
      return Array.from(this.visibleCaseIds).filter(caseId => self.dataset?.isCaseSelected(caseId))
    },
    /**
     * This returns an array of the visible cases that are unselected.
     * If displayOnlySelectedCases is enabled, this will return an empty set.
     */
    get unselectedCases() {
      if (!self.dataset) return []
      // O(visible cases): avoid observing this per selection change (see the flag-gated reactions).
      return Array.from(this.visibleCaseIds).filter(caseId => !self.dataset?.isCaseSelected(caseId))
    }
  }))
  .views(self => ({
    // Note that we have to go through each of the filteredCases in order to return all the values
    valuesForAttrRole: cachedFnWithArgsFactory({
      key: (role: AttrRole) => role,
      calculate: (role: AttrRole) => {
        const attrID = self.attributeID(role)
        const dataset = self.dataset
        const allCaseIDs = Array.from(self.visibleCaseIds)
        const allValues = attrID ? allCaseIDs.map((anID: string) => dataset?.getStrValue(anID, attrID)) : []
        return allValues.filter(aValue => aValue) as string[]
      },
      name: "valuesForAttrRole"
    })
  }))
  .views(self => ({
    numericValuesForAttribute: cachedFnWithArgsFactory({
      key: (attrID: string, type?: AttributeType) => `${attrID}:${type || ""}`,
      calculate: (attrID: string, type?: AttributeType) => {
        const dataset = self.dataset
        const allCaseIDs = Array.from(self.visibleCaseIds)
        const allValues = attrID
          ? allCaseIDs.map((anID: string) => {
            const value = dataDisplayGetNumericValue(dataset, anID, attrID, type === "numeric")
            return isFiniteNumber(value) ? value : null
          }) : []
        return allValues.filter(aValue => aValue != null)
      },
      name: "numericValuesForAttribute"
    })
  }))
  .views(self => ({
    /**
     * This can be called here or in derived classes.
     * This returns just values which can be converted to numbers for the
     * attribute of this role. It does not include all cases, see `visibleCaseIds`.
     * TODO: it seems better if this included unselected cases when displayOnlySelectedCases is enabled
     */
    _numericValuesForAttrRole(role: AttrRole) {
      const attrID = self.attributeID(role)
      const type = self.attributeType(role)
      return self.numericValuesForAttribute(attrID || '', type)
    },
    /**
     * This is overridden in derived class where we need to handle multiple y attributes.
     */
    numericValuesForAttrRole(role: AttrRole) {
      return this._numericValuesForAttrRole(role)
    },
    categorySetForAttrRole(role: AttrRole) {
      if (self.metadata) {
        const attributeID = self.attributeID(role) || ''
        return self.metadata.getCategorySet(attributeID)
      }
    },
    get categoricalRoles(): AttrRole[] {
      return self.attributeType("legend") === "categorical" ? ["legend"] : []
    }
  }))
  .views(self => ({
    /**
     * @param role
     * @param emptyCategoryArray
     */
    categoryArrayForAttrRole: cachedFnWithArgsFactory<(role: AttrRole, emptyCategoryArray?: string[]) => string[]>({
      key: (role: AttrRole, emptyCategoryArray = [kMain]) => JSON.stringify({ role, emptyCategoryArray }),
      calculate: (role: AttrRole, emptyCategoryArray = [kMain]) => {
        const valuesSet = new Set(self.valuesForAttrRole(role)),
          categoryLimitForRole = self.numberOfCategoriesLimitByRole.get(role)
        if (valuesSet.size === 0) return emptyCategoryArray

        let resultArray: string[] = []
        // category set maintains the canonical order of categories
        const allCategorySet = self.categorySetForAttrRole(role)
        // if we don't have a category set just return the values
        if (!allCategorySet && valuesSet.size > 0) {
          resultArray = Array.from(valuesSet)
        }
        else {
          // return the categories in canonical order
          allCategorySet?.values.forEach(category => {
            if (valuesSet.has(category)) {
              resultArray.push(category)
            }
          })
          if (categoryLimitForRole && resultArray.length > categoryLimitForRole) {
            resultArray.length = categoryLimitForRole
            resultArray[categoryLimitForRole - 1] = kOther
          }
        }
        return resultArray
      },
      name: "categoryArrayForAttrRole"
    }),
    get allCategoriesForRoles() {
      const categories: Map<AttrRole, string[]> = new Map()
      const roles = self.categoricalRoles
      roles.forEach(role => {
        const categorySet = self.categorySetForAttrRole(role)
        if (categorySet) {
          categories.set(role, categorySet.valuesArray)
        }
      })
      return categories
    }
  }))
  .views(self => ({
    getUnsortedCaseDataArray(caseArrayNumber: number): CaseData[] {
      return (self.filteredCases[caseArrayNumber]?.caseIds || []).map(id => {
        return {plotNum: caseArrayNumber, caseID: id}
      })
    }
  }))
  .views(self => ({
    // Note that we have to go through each of the filteredCases in order to return all the values
    getCaseDataArray: cachedFnWithArgsFactory({
      key: (caseArrayNumber: number) => String(caseArrayNumber),
      calculate: (caseArrayNumber: number) => {
        const caseDataArray = self.getUnsortedCaseDataArray(caseArrayNumber),
          legendAttrID = self.attributeID('legend')
        if (legendAttrID) {
          const legendType = self.attributeType("legend")
          if (legendType === "numeric" || legendType === "date") {
            // dataDisplayGetNumericValue returns epoch-seconds for date attrs, so the same
            // numeric comparator orders date legends chronologically.
            caseDataArray.sort((cd1: CaseData, cd2: CaseData) => {
              const cd1Value = dataDisplayGetNumericValue(self.dataset, cd1.caseID, legendAttrID, true) ?? NaN,
                cd2Value = dataDisplayGetNumericValue(self.dataset, cd2.caseID, legendAttrID, true) ?? NaN
              return numericSortComparator({a: cd1Value, b: cd2Value, order: "desc"})
            })
          } else {
            const categories = Array.from(self.categoryArrayForAttrRole('legend'))
            caseDataArray.sort((cd1: CaseData, cd2: CaseData) => {
              const cd1Value = self.dataset?.getStrValue(cd1.caseID, legendAttrID) ?? '',
                cd2Value = self.dataset?.getStrValue(cd2.caseID, legendAttrID) ?? ''
              return categories.indexOf(cd1Value) - categories.indexOf(cd2Value)
            })
          }
        }
        return caseDataArray
      },
      name: "getCaseDataArray"
    }),
    get joinedCaseDataArrays() {
      const joinedCaseData: CaseData[] = []
      self.filteredCases.forEach((aFilteredCases, index) => {
          aFilteredCases.caseIds.forEach(
            (id) => joinedCaseData.push({plotNum: index, caseID: id}))
        }
      )
      return joinedCaseData
    },
    get lowColor() {
      const attrId = self.attributeID("legend")
      return self.metadata?.getAttributeColorRange(attrId)?.low ?? kDefaultLowAttributeColor
    },
    get highColor() {
      const attrId = self.attributeID("legend")
      return self.metadata?.getAttributeColorRange(attrId)?.high ?? kDefaultHighAttributeColor
    }
  }))
  .views(self => ({
    // observable hash of rendered case ids
    get caseDataHash() {
      return hashStringSets(self.filteredCases.map(cases => cases.caseIds))
    },
    // Effective number of legend bins: the per-attribute override (default kDefaultLegendBinCount),
    // clamped to [2, cap] where cap = min(#points, #distinct values). A degenerate legend (<=1
    // distinct value) collapses to a single bin/color.
    get legendBinCount() {
      const values = self.numericValuesForAttrRole("legend") ?? []
      const cap = Math.min(values.length, new Set(values).size)
      if (cap < 2) return 1
      const legendAttrId = self.attributeID("legend")
      // Coerce to a finite integer so the reported count matches the rendered color-ramp length
      // (getChoroplethColors truncates a fractional length); a non-finite stored value (e.g. from a
      // legacy/hand-edited snapshot that bypassed the normalizing setter) falls back to the default.
      const stored = self.metadata?.getAttributeBinCount(legendAttrId)
      const requested = stored != null && Number.isFinite(stored) ? Math.round(stored) : kDefaultLegendBinCount
      return Math.max(2, Math.min(requested, cap))
    },
    get choroplethColors() {
      return getChoroplethColors(
        self.lowColor,
        self.highColor,
        this.legendBinCount
      )
    }
  }))
  .views(self => ({
    // The case ids whose legend value is `cat`. Grouping cases by legend value is O(cases) (a
    // getStrValue per case), so it is cached here and invalidated via clearCasesCache (data/legend/
    // case changes) — but NOT on selection changes, since category membership doesn't depend on the
    // selection. allCasesForCategoryAreSelected then only pays the cheap per-case isCaseSelected check
    // on each selection change (e.g. every marquee move) instead of re-grouping every case.
    caseIdsForCategory: cachedFnWithArgsFactory<(cat: string) => string[]>({
      key: (cat: string) => cat,
      calculate: (cat: string) => {
        const dataset = self.dataset
        const legendID = self.attributeID('legend')
        return (legendID && self.getCaseDataArray(0).filter((aCaseData: CaseData) =>
          dataset?.getStrValue(aCaseData.caseID, legendID) === cat
        ).map((aCaseData: CaseData) => aCaseData.caseID)) || []
      },
      name: "caseIdsForCategory"
    }),
    // The effective numeric-legend display range: the per-attribute Min/Max override where set, else
    // the live data extent. A reversed/degenerate effective range (e.g. an orphaned override after
    // the other bound was cleared) falls back to the data extent. This drives both the quantize color
    // scale's domain and the legend's displayed endpoint labels, so the legend always matches the
    // Min/Max the user set -- including in quantile mode, whose trained domain is the data quantiles
    // rather than the override (CODAP-1292).
    get legendNumericRange(): { min?: number, max?: number } {
      const values = self.numericValuesForAttrRole("legend") ?? []
      const [dataMin, dataMax] = extent(values)
      const legendAttrId = self.attributeID("legend")
      const { min: overrideMin, max: overrideMax } =
        self.metadata?.getAttributeLegendRange(legendAttrId) ?? {}
      let min = overrideMin ?? dataMin
      let max = overrideMax ?? dataMax
      if (min != null && max != null && min >= max) {
        min = dataMin
        max = dataMax
      }
      return { min, max }
    },
    // The positive log domain for a logarithmic legend: upper bound = a positive Max override else the
    // positive data max; lower bound = a positive Min override else the smallest positive value within
    // the range (an override <= 0 is invalid for a log scale and ignored). An invalid/reversed override
    // (min >= max) falls back to the positive data extent rather than blanking the legend (mirroring
    // legendNumericRange); only a data set with no usable positive range (<= 1 distinct positive value)
    // returns {} (degenerate -> single bin / blank legend).
    get legendLogDomain(): { min?: number, max?: number } {
      const values = self.numericValuesForAttrRole("legend") ?? []
      const legendAttrId = self.attributeID("legend")
      const { min: overrideMin, max: overrideMax } =
        self.metadata?.getAttributeLegendRange(legendAttrId) ?? {}
      const [, dataMax] = extent(values)
      // Smallest positive value <= cap, found in a single pass; a spread (Math.min(...values)) would
      // expand the whole array into call arguments and can overflow for large datasets.
      const smallestPositiveUpTo = (cap: number) => {
        let smallest: number | undefined
        for (const v of values) {
          if (v > 0 && v <= cap && (smallest == null || v < smallest)) smallest = v
        }
        return smallest
      }
      let max = overrideMax != null && overrideMax > 0 ? overrideMax : dataMax
      let min = overrideMin != null && overrideMin > 0
        ? overrideMin
        : max != null ? smallestPositiveUpTo(max) : undefined
      // A reversed/invalid override range falls back to the positive data extent rather than blanking.
      if (min == null || max == null || max <= 0 || min >= max) {
        max = dataMax
        min = max != null && max > 0 ? smallestPositiveUpTo(max) : undefined
      }
      if (min == null || max == null || max <= 0 || min >= max) return {}
      return { min, max }
    },
    get legendIsLogarithmic(): boolean {
      return self.metadata?.getAttributeBinningType(self.attributeID("legend")) === "logarithmic"
    },
    // Endpoint labels to display: the log domain in logarithmic mode, else the linear/quantile range.
    get legendDisplayRange(): { min?: number, max?: number } {
      return this.legendIsLogarithmic ? this.legendLogDomain : this.legendNumericRange
    },
    get legendNumericColorScale() {
      // TODO: Handle the displayOnlySelectedCases better. What we would like to do is
      // to basically ignore displayOnlySelectedCases when computing the legend bins.
      // This way the legend will not jump around when the user is selecting different
      // cases when in displayOnlySelectedCases mode.
      // There are several criteria besides displayOnlySelectedCases which impact which
      // cases are shown on the visualization:
      // - set aside cases in the dataset should be excluded
      // - cases filtered by the filter formula in the dataset should be excluded
      // - cases hidden in the visualization should be excluded
      // - cases filtered by the filter formula in the visualization should be excluded
      // - cases that are not plottable on at least one of the plots of the visualization
      //   should be excluded
      // All of these criteria are handled by numericValuesForAttrRole("legend") but it also
      // excludes unselected cases if displayOnlySelectedCases is enabled.
      // It would make sense for numericValuesForAttrRole to ignore the
      // displayOnlySelectedCases criteria. It is used here and also to compute the axis extents.
      // The axes should also not jump around when using displayOnlySelectedCases.
      // Implementing this is hard because of the last bullet. Handling the "not plottable"
      // cases is done by the FilteredCases system which is overridden by the
      // GraphDataConfigurationModel in order to handle graphs with multiple y axes. This
      // FilterCases system is also what implements displayOnlySelectedCases.
      // The best solution might be to separate the displayOnlySelectedCases from FilterCases,
      // perhaps by renaming it PlottableCases and then apply the displayOnlySelectedCases
      // criteria further up chain of filters.
      if (self.legendQuantilesAreLocked && self._legendNumericColorScale) {
        return self._legendNumericColorScale
      }
      const binningType = self.metadata?.getAttributeBinningType(self.attributeID("legend"))
      switch (binningType) {
        case "quantize": {
          // Use the shared effective display range (override ?? data extent, with reversed-range
          // fallback) so the quantize domain and the legend's displayed endpoints stay in lockstep.
          const { min: effectiveMin, max: effectiveMax } = this.legendNumericRange
          if (effectiveMin == null || effectiveMax == null) {
            return scaleQuantize([], self.choroplethColors)
          }
          // scaleQuantize maps out-of-domain values to the nearest range extreme automatically
          return scaleQuantize([effectiveMin, effectiveMax], self.choroplethColors)
        }
        case "logarithmic": {
          const { min: logMin, max: logMax } = this.legendLogDomain
          const colors = self.choroplethColors
          // Degenerate range -> single bin (threshold scale with no cut points, one color).
          if (logMin == null || logMax == null) {
            return scaleThreshold<number, string>().domain([]).range([colors[0]])
          }
          const n = colors.length // == legendBinCount
          const ratio = logMax / logMin
          // n-1 equal-ratio cut points: t_i = logMin * ratio^(i/n)
          const thresholds = Array.from({ length: n - 1 }, (_, i) => logMin * Math.pow(ratio, (i + 1) / n))
          return scaleThreshold<number, string>().domain(thresholds).range(colors)
        }
        case "quantile":
        default:
          // d3 quantile scale for non-degenerate data; equal-frequency repair when ties collapse it.
          return this.legendQuantileScaleAndExtents.scale
      }
    },
    // Quantile binning, including the degenerate-tie repair. Computed once and shared by the color
    // scale (which needs the scale) and the legend (which needs per-bin data extents for labels).
    // d3's scaleQuantile is used unchanged for non-degenerate data (byte-identical to V2); when
    // ties collapse the quantile boundaries into empty bins, a scaleThreshold built from the
    // most-equal partition groups replaces it so distinct values get distinct colors and no bin
    // is empty.
    get legendQuantileScaleAndExtents(): {
      scale: ScaleQuantile<string> | ScaleThreshold<number, string>,
      extents?: Array<{ min: number, max: number }>
    } {
      const legendAttrId = self.attributeID("legend")
      const values = self.numericValuesForAttrRole("legend") ?? []
      const { min: overrideMin, max: overrideMax } =
        self.metadata?.getAttributeLegendRange(legendAttrId) ?? {}
      const filtered = overrideMin == null && overrideMax == null
        ? values
        : values.filter(v =>
            (overrideMin == null || v >= overrideMin) && (overrideMax == null || v <= overrideMax))
      // If the override range excludes every value (orphaned/stale override, or the data moved
      // outside the range), train on the full value set rather than producing an all-first-color scale.
      const trained = filtered.length > 0 ? filtered : values
      const scale = scaleQuantile(trained, self.choroplethColors)
      // Degenerate iff [min, ...quantiles] is not strictly increasing: a duplicate boundary means a
      // bin has no values. (The last bin always contains the max, so only the lower boundaries matter.)
      const boundaries = [scale.domain()[0], ...scale.quantiles()]
      const degenerate = trained.length > 0 &&
        boundaries.some((b, i) => i > 0 && b <= boundaries[i - 1])
      if (!degenerate) return { scale }
      const bins = equalFrequencyBins(trained, self.legendBinCount)
      const thresholds = bins.slice(1).map(b => b.min)
      // bins.length can be < legendBinCount when an override leaves fewer distinct values than the
      // bin-count cap; size the color ramp to the actual bin count so the threshold scale's range
      // length matches its domain.
      const colors = bins.length === self.legendBinCount
        ? self.choroplethColors
        : getChoroplethColors(self.lowColor, self.highColor, bins.length)
      const threshScale = scaleThreshold<number, string>().domain(thresholds).range(colors)
      return { scale: threshScale, extents: bins.map(b => ({ min: b.min, max: b.max })) }
    },
    // Per-bin data extents for labeling the legend, defined only for the degenerate quantile case
    // (where bins must be labeled from the data, e.g. "1" or "2 - 3"). undefined everywhere else,
    // so the return value doubles as the "is degenerate" signal the legend keys off. Locked legends
    // use their frozen threshold labels, so they opt out here.
    get legendBinDataExtents(): Array<{ min: number, max: number }> | undefined {
      if (self.legendQuantilesAreLocked) return undefined
      const binningType = self.metadata?.getAttributeBinningType(self.attributeID("legend"))
      if (binningType != null && binningType !== "quantile") return undefined
      return this.legendQuantileScaleAndExtents.extents
    },
  }))
  .views(self => (
    {
      getLegendColorForCategory(cat: string): string {
        const categorySet = self.categorySetForAttrRole('legend')
        return categorySet?.colorForCategory(cat) ?? missingColor
      },

      // `shapeIfUnset` is the display's own shape, so a category the user has not assigned one to
      // keeps drawing whatever the plot drew before a legend was added.
      getLegendShapeForCategory(cat: string, shapeIfUnset: PointShape = kDefaultPointShape): PointShape {
        const categorySet = self.categorySetForAttrRole('legend')
        return categorySet?.shapeForCategory(cat, shapeIfUnset) ?? shapeIfUnset
      },

      getLegendColorForNumericValue(value: number): string {
        // A log scale is undefined for values <= 0; they are always missing, including when the log
        // domain is degenerate (<= 1 distinct positive value) and legendDisplayRange is empty.
        if (self.legendIsLogarithmic && value <= 0) return missingColor
        // When the effective range is defined, values outside it are "missing" rather than clamped
        // into an end bin. legendDisplayRange is the same range used for the endpoint labels, so
        // colors and labels stay in lockstep. A degenerate domain yields no min/max but still
        // produces a valid single-bin scale, so skip the check there and let the scale color it.
        const { min, max } = self.legendDisplayRange
        if (min != null && max != null && (value < min || value > max)) return missingColor
        return self.legendNumericColorScale(value)
      },

      getLegendColorForDateValue(value: string): string {
        const dateValueArray = stringValuesToDateSeconds([value])
        return self.legendNumericColorScale(dateValueArray[0])
      },

      getCasesForCategoryValues(
        primaryAttrRole: AttrRole, primaryValue: string, secondaryValue?: string, primarySplitValue?: string,
        secondarySplitValue?: string, legendCat?: string, extend = false
      ) {
        const dataset = self.dataset,
          primaryAttrID = self.attributeID(primaryAttrRole),
          secondaryAttrRole = primaryAttrRole === "x" ? "y" : "x",
          extraPrimaryAttrRole = primaryAttrRole === "x" ? "topSplit" : "rightSplit",
          extraSecondaryAttrRole = primaryAttrRole === "x" ? "rightSplit" : "topSplit",
          secondaryAttrID = self.attributeID(secondaryAttrRole),
          extraPrimaryAttrID = self.attributeID(extraPrimaryAttrRole),
          extraSecondaryAttrID = self.attributeID(extraSecondaryAttrRole)

        return primaryAttrID
          ? self.getCaseDataArray(0).filter((aCaseData: CaseData) => {
            return dataset?.getStrValue(aCaseData.caseID, primaryAttrID) === primaryValue &&
              (secondaryValue === kMain ||
                dataset?.getStrValue(aCaseData.caseID, secondaryAttrID) === secondaryValue) &&
              (primarySplitValue === kMain ||
                dataset?.getStrValue(aCaseData.caseID, extraPrimaryAttrID) === primarySplitValue) &&
              (secondarySplitValue === kMain ||
                dataset?.getStrValue(aCaseData.caseID, extraSecondaryAttrID) === secondarySplitValue) &&
              (!legendCat ||
                dataset?.getStrValue(aCaseData.caseID, self.attributeID("legend")) === legendCat)
          }).map((aCaseData: CaseData) => aCaseData.caseID)
          : []
      },

      getCasesForLegendValue(aValue: string) {
        const dataset = self.dataset,
          legendID = self.attributeID('legend'),
          collectionGroup = dataset?.getCollectionForAttribute(legendID || '')
        let caseIDs: string[] = []
        if (collectionGroup) {
          const parentCases = dataset?.getCasesForCollection(collectionGroup.id)
          parentCases?.forEach((aCase: ICase) => {
            if (dataset?.getStrValue(aCase.__id__, legendID || '') === aValue) {
              caseIDs?.push(aCase.__id__)
            }
          })
        } else {
          caseIDs = legendID ? self.getCaseDataArray(0).filter((aCaseData: CaseData) => {
              return dataset?.getStrValue(aCaseData.caseID, legendID) === aValue
            }).map((aCaseData: CaseData) => aCaseData.caseID)
            : []
        }
        return caseIDs
      },
      allCasesForCategoryAreSelected: cachedFnWithArgsFactory({
        key: (cat: string) => cat,
        calculate: (cat: string) => {
          const dataset = self.dataset
          const caseIds = self.caseIdsForCategory(cat)
          return caseIds.length > 0 && caseIds.every((anID: string) => dataset?.isCaseSelected(anID))
        },
        name: "allCasesForCategoryAreSelected"
      }),
      getCasesInLegendRange(min: number, max: number, inclusiveMax = false) {
        const dataset = self.dataset
        const legendID = self.attributeID('legend')
        const typeIsNumeric = self.attributeType('legend') === 'numeric'
        return legendID
          ? self.getCaseDataArray(0).filter((aCaseData: CaseData) => {
            const value = dataDisplayGetNumericValue(dataset, aCaseData.caseID, legendID, typeIsNumeric)
            return value != null && value >= min && (inclusiveMax ? value <= max : value < max)
          }).map((aCaseData: CaseData) => aCaseData.caseID)
          : []

      }
    }))
  .views(self => (
    {
      getCasesForLegendBin(bin: number) {
        const scale = self.legendNumericColorScale
        const thresholds = getScaleThresholds(scale)
        // Out-of-range values are "missing" (not in any bin), so the end bins are bounded by the
        // effective legend range rather than ±Infinity: the first bin starts at the range min and
        // the last bin ends at the range max, inclusive (the max is a real, plottable value). In
        // logarithmic mode with a degenerate domain (no rangeMin) the first bin still starts just
        // above 0, since non-positive values are missing rather than part of the bin.
        const { min: rangeMin, max: rangeMax } = self.legendDisplayRange
        const isLastBin = bin === thresholds.length
        // Number.MIN_VALUE is the smallest *positive* double, so it floors the first log bin just above 0.
        const firstBinMin = rangeMin ?? (self.legendIsLogarithmic ? Number.MIN_VALUE : -Infinity)
        const min = bin === 0 ? firstBinMin : thresholds[bin - 1]
        const max = isLastBin ? (rangeMax ?? Infinity) : thresholds[bin]
        return self.getCasesInLegendRange(min, max, isLastBin)
      }
    }))
  .views(self => (
    {
      casesInBinAreSelected(quantile: number): boolean {
        const selection = self.getCasesForLegendBin(quantile)
        return !!(selection.length > 0 && selection?.every((anID: string) => self.dataset?.isCaseSelected(anID)))
      },
      /*
       * Whether the legend assigns per-category state. Note this is narrower than
       * isCategoricalAttributeType, which counts a color legend too: that one gives each case a
       * color of its own, so there are no categories to hang a color or a shape on.
       */
      get legendHasCategories(): boolean {
        const legendType = self.attributeType('legend')
        return legendType === 'categorical' || legendType === 'checkbox'
      },
      /*
       * Whether the legend attribute lives in a collection more childmost than the plotted cases.
       * A point then stands for several children at once, and resolving the legend through any one
       * of them would attribute that child's value to the whole group, so callers fall back.
       *
       * Must stay a block above the views that use it, so they reach it through `self`. They are
       * passed around as detached function references -- a plot hands `getLegendColorForCase` to
       * setPointCoordinates, which calls it bare -- so `this` inside them is undefined.
       */
      get legendCollectionIsMoreChildmost(): boolean {
        const legendID = self.attributeID('legend')
        const legendCollectionID = self.dataset?.getCollectionForAttribute(legendID)?.id
        const legendCollectionIndex = self.dataset?.getCollectionIndex(legendCollectionID) ?? 0
        const childmostCollectionID = idOfChildmostCollectionForAttributes(self.axisAttributeIDs, self.dataset)
        const childmostCollectionIndex = self.dataset?.getCollectionIndex(childmostCollectionID) ?? 0
        return legendCollectionIndex > childmostCollectionIndex
      }
    }))
  .views(self => (
    {
      /**
       * Called to determine whether the categories on an axis should be centered.
       */
      categoriesForAxisShouldBeCentered(place: AxisPlace) {
        return true
      },
      placeCanHaveZeroExtent(place: GraphPlace) {
        return ['rightNumeric', 'legend', 'top', 'rightCat'].includes(place) &&
          !self.attributeID(graphPlaceToAttrRole[place])
      },
      // GraphDataConfigurationModel overrides this. Here we only have to worry about the 'legend' role.
      placeCanAcceptAttributeIDDrop(
        place: GraphPlace, dataSet?: IDataSet, idToDrop?: string,
        options?: { allowSameAttr?: boolean }
      ) {
        if (idToDrop) {
          const desc = self.attributeDescriptionForRole('legend')
          return options?.allowSameAttr || desc?.attributeID !== idToDrop
        }
        return false
      },
      /**
       * This is a domain which can be monitored by reactions without having to
       * request the color for every case to see if the colors have changed.
       * For numeric and date it is an actual d3 color scale.
       * For categorical it is a map of categories to colors
       * The color type is not handled yet.
       */
      // Changes identity when any category's shape changes, so a display can react to it the way it
      // reacts to legendColorDomain.
      get legendShapeDomain() {
        if (!self.legendHasCategories) return undefined
        return self.categorySetForAttrRole('legend')?.shapeMap
      },
      get legendColorDomain() {
        const legendType = self.attributeType('legend')
        switch (legendType) {
          case "categorical": {
            const categorySet = self.categorySetForAttrRole('legend')
            return categorySet?.colorMap
          }
          case "numeric":
          case "date":
            return self.legendNumericColorScale
          case "color":
            // TODO: we need to be watching for any changes to the legend attribute values
            return 0
          default:
            return 0
        }
      },
      getLegendColorForCase(id: string, colorIfMissing = missingColor): string {
        /*
         * Answered before the checks below, which read attributeID and so answer differently by
         * display: the base filters an unusable assignment out, a graph's override keeps it. Without
         * this a map would fall through to its own point color and draw normally colored points
         * under a legend saying the attribute cannot distinguish them.
         */
        if (id && self.legendAttributeIsInoperable) {
          return colorIfMissing
        }
        const legendID = self.attributeID('legend')
        // todo: When user deletes we are not currently deleting the legend attribute ID. But we should.
        const legendAttribute = self.dataset?.getAttribute(legendID)
        if (!id || !legendID || !legendAttribute) {
          return ''
        }
        const legendType = self.attributeType('legend')
        const legendValue = self.dataset?.getStrValue(id, legendID)
        if (!legendValue) {
          return colorIfMissing
        }
        switch (legendType) {
          case 'categorical':
            return self.getLegendColorForCategory(legendValue)
          case 'numeric':
            return self.getLegendColorForNumericValue(Number(legendValue))
          case 'date':
            return self.getLegendColorForDateValue(legendValue)
          case 'color':
            return parseColor(legendValue, { colorNames: true }) ? legendValue : colorIfMissing
          case 'checkbox':
            return self.getLegendColorForCategory(legendValue)
          default:
            return ''
        }
      },
      // `shapeIfNoCategory` is the display's own shape, which is what a plot with no legend draws
      // throughout.
      getLegendShapeForCase(id: string, shapeIfNoCategory: PointShape = kDefaultPointShape): PointShape {
        const legendID = self.attributeID('legend')
        const legendAttribute = self.dataset?.getAttribute(legendID)
        if (!id || !legendID || !legendAttribute) return shapeIfNoCategory

        if (!self.legendHasCategories) return shapeIfNoCategory

        // This is the live check for shape, where the color path's equivalent is unreachable: color
        // answers the inoperable case up front, and this returns on an empty legendID before it
        // could. Both amount to falling back rather than speaking for a group.
        if (self.legendCollectionIsMoreChildmost) return shapeIfNoCategory

        const legendValue = self.dataset?.getStrValue(id, legendID)
        if (!legendValue) return shapeIfNoCategory

        return self.getLegendShapeForCategory(legendValue, shapeIfNoCategory)
      }
    }))
  .actions(self => ({
    clearCasesCache() {
      self.valuesForAttrRole.invalidateAll()
      self.numericValuesForAttribute.invalidateAll()
      self.categoryArrayForAttrRole.invalidateAll()
      self.caseIdsForCategory.invalidateAll()
      self.allCasesForCategoryAreSelected.invalidateAll()
      self.getCaseDataArray.invalidateAll()
      // increment observable change count
      ++self.casesChangeCount
    }
  }))
  .actions(self => ({
    /**
     * This is called when the user swaps categories in the legend, but not when the user swaps categories
     * by dragging categories on an axis.
     * @param role
     */
    storeAllCurrentColorsForAttrRole(role: AttrRole) {
      const categorySet = self.categorySetForAttrRole(role)
      if (categorySet) {
        categorySet.storeAllCurrentColors()
      }
    },
    // This will only swap the categories if they are neighbors.
    // If the categories are not next to each other the behavior is complex,
    // so it is best to read the code to understand what will happen.
    swapCategoriesForAttrRole(role: AttrRole, catIndex1: number, catIndex2: number) {
      const categoryArray = self.categoryArrayForAttrRole(role),
        numCategories = categoryArray.length,
        categorySet = self.categorySetForAttrRole(role)
      if (catIndex2 < catIndex1) {
        const temp = catIndex1
        catIndex1 = catIndex2
        catIndex2 = temp
      }
      if (categorySet && numCategories > catIndex1 && numCategories > catIndex2) {
        const cat1 = categoryArray[catIndex1],
          beforeCat = catIndex2 < numCategories - 1 ? categoryArray[catIndex2 + 1] : undefined
        categorySet.move(cat1, beforeCat)
      }
    },
    handleSetCaseValues(actionCall: ISerializedActionCall, cases: IFilteredChangedCases) {
      if (!isCaseValueChangeAction(actionCall)) return
      const [affectedCases] = actionCall.args
      // this is called by the FilteredCases object with additional information about
      // whether the value changes result in adding/removing any cases from the filtered set
      // a single call to setCaseValues can result in up to three calls to the handlers
      if (cases.added.length) {
        const newCases = self.dataset?.getItems(cases.added)
        self.handlers.forEach(handler => handler({name: "addCases", args: [newCases]}))
      }
      if (cases.removed.length) {
        self.handlers.forEach(handler => handler({name: "removeCases", args: [cases.removed]}))
      }
      if (cases.changed.length) {
        const idSet = new Set(cases.changed)
        const changedCases = affectedCases.filter(aCase => idSet.has(aCase.__id__))
        self.handlers.forEach(handler => handler({name: "setCaseValues", args: [changedCases]}))
        ++self.casesChangeCount
      }
    },
    _updateFilteredCasesCollectionID() {
      const childmostCollectionID = idOfChildmostCollectionForAttributes(self.axisAttributeIDs, self.dataset)
      self.filteredCases.forEach((aFilteredCases) => {
        aFilteredCases.setCollectionID(childmostCollectionID)
      })
    },
    invalidateCases() {
      self.filteredCases.forEach((aFilteredCases) => {
        aFilteredCases.invalidateCases()
      })
      self.clearCasesCache()
    },
    _addNewFilteredCases() {
      if (self.dataset) {
        this._updateFilteredCasesCollectionID()
        self.filteredCases.push(new FilteredCases({
          source: self.dataset,
          casesArrayNumber: self.filteredCases.length,
          filter: self.filterCase,
          collectionID: idOfChildmostCollectionForAttributes(self.axisAttributeIDs, self.dataset),
          onSetCaseValues: this.handleSetCaseValues
        }))
        self.setPointsNeedUpdating(true)
      }
    },
    _clearFilteredCases(dataset: IDataSet | undefined) {
      self.filteredCases.forEach((aFilteredCases) => {
        aFilteredCases.destroy()
      })
      self.filteredCases.clear()
      if (dataset) {
        self.filteredCases[0] = new FilteredCases({
          source: dataset,
          filter: self.filterCase,
          collectionID: idOfChildmostCollectionForAttributes(self.axisAttributeIDs, dataset),
          onSetCaseValues: this.handleSetCaseValues
        })
      }
    },
    _setAttributeType(type: AttributeType, plotNumber = 0) {
      self.filteredCases?.forEach((aFilteredCases) => {
        aFilteredCases.invalidateCases()
      })
    },
    handleDataSetAction(actionCall: ISerializedActionCall) {
      // setCaseValues, setComputedCaseValues, and removeAttribute self-invalidate via the dataset's
      // own invalidateCases call, which we observe reactively (see the dataset.itemIds reaction).
      // addCases and removeCases don't currently self-invalidate, so we have to do it here.
      const cacheClearingActions = ["addCases", "removeCases"]
      if (cacheClearingActions.includes(actionCall.name)) {
        // Suppress animation when cases are removed, or added as part of a high-speed stream
        // (the addCases suppressAnimation option, e.g. coalesced plugin creates, CODAP-1408),
        // so points/bars update instantly rather than animating. This is essential while data
        // streams in: otherwise each added case restarts a fresh transition every frame, and
        // because the transition clock resets each time, existing points never advance toward
        // their new positions until streaming stops. Individual adds (user-entered rows, paced
        // plugin creates) animate as usual. matchCirclesToData consumes this flag to stop any
        // in-flight animation and skip startAnimation. (See updateFilterFormulaResults for the
        // analogous slider/filter case.)
        const suppressOnAdd = isAddCasesAction(actionCall) && actionCall.args[1]?.suppressAnimation
        if (actionCall.name === "removeCases" || suppressOnAdd) {
          self.suppressAnimation = true
        }
        this.invalidateCases()
      }
      // forward all actions from dataset except "setCaseValues" which requires intervention
      if (actionCall.name === "setCaseValues") return
      self.handlers.forEach(handler => handler(actionCall))
    },
  }))
  .actions(self => ({
    setLegendQuantiles(quantiles: number[]) {
      self.legendQuantiles.replace(quantiles)
    },
    setLegendQuantilesAreLocked(areLocked: boolean | undefined) {
      if (areLocked === self.legendQuantilesAreLocked) {
        return
      }
      if (areLocked) {
        const scale = self.legendNumericColorScale
        const thresholds = getScaleThresholds(scale)
        this.setLegendQuantiles(thresholds)
        self._legendNumericColorScale = scale
      }
      else {
        self._legendNumericColorScale = null
        this.setLegendQuantiles([])
      }
      self.legendQuantilesAreLocked = areLocked
    },
    setLegendColorForCategory(cat: string, color: string) {
      const categorySet = self.categorySetForAttrRole('legend')
      categorySet?.setColorForCategory(cat, color)
    },
    setLegendShapeForCategory(cat: string, shape: PointShape) {
      const categorySet = self.categorySetForAttrRole('legend')
      categorySet?.setShapeForCategory(cat, shape)
    },
    setNumberOfCategoriesLimitForRole(role: AttrRole, limit: number | undefined) {
      if (limit !== undefined && limit <= 0) {
        limit = undefined
      }
      self.numberOfCategoriesLimitByRole.set(role, limit)
      self.categoryArrayForAttrRole.invalidate(role)
    },
  }))
  .actions(self => ({
    clearFilterFormula() {
      self.filterFormula = undefined
      self.filteredOutCaseIds.clear()
      self.filterFormulaError = ""
      self.invalidateCases()
    }
  }))
  .actions(self => ({
    setFilterFormula(display: string) {
      if (display) {
        if (!self.filterFormula) {
          self.filterFormula = Formula.create({ display })
        }
        else {
          self.filterFormula.setDisplayExpression(display)
        }
      } else {
        self.clearFilterFormula()
      }
    },
    updateFilterFormulaResults(filterFormulaResults: { itemId: string, result: boolean }[], { replaceAll = false }) {
      if (replaceAll) {
        self.filteredOutCaseIds.clear()
        // Suppress animation for actual filter formula recalculations so points/bars update instantly
        // (e.g., when a slider value changes and the filter formula is re-evaluated for all cases).
        // Don't suppress for cleanup calls that pass an empty array.
        if (filterFormulaResults.length > 0) {
          self.suppressAnimation = true
        }
      }
      filterFormulaResults.forEach(({ itemId, result }) => {
        if (result === false) {
          self.filteredOutCaseIds.add(itemId)
        }
        else {
          self.filteredOutCaseIds.delete(itemId)
        }
      })
      self.invalidateCases()
    },
    setFilterFormulaError(error: string) {
      self.filterFormulaError = error
    },
    setSuppressAnimation(suppress: boolean) {
      self.suppressAnimation = suppress
    }
  }))
  .actions(self => ({
    handleDataSetChange(data?: IDataSet) {
      self.actionHandlerDisposer?.()
      self.actionHandlerDisposer = data ? onAnyAction(data, self.handleDataSetAction) : undefined
      self._clearFilteredCases(data)
    }
  }))
  .actions(self => ({
    afterCreate() {
      if (self.legendQuantilesAreLocked && self.legendQuantiles.length > 0) {
        self._legendNumericColorScale = scaleThreshold(self.legendQuantiles, self.choroplethColors)
      }
      // respond to change of dataset
      addDisposer(self, reaction(
        () => self.dataset,
        data => self.handleDataSetChange(data),
        {name: "DataConfigurationModel.afterCreate.reaction [dataset]", fireImmediately: true }
      ))
      // respond to change of allCategoriesForRoles
      addDisposer(self, reaction(
        () => self.allCategoriesForRoles,
        () => self.invalidateCases(),
        {
          name: "DataConfigurationModel.afterCreate.reaction [allCategoriesForRoles]",
          equals: comparer.structural
        }
      ))
      addDisposer(self, reaction(
        () => {
          const legendCategorySet = self.categorySetForAttrRole("legend")
          return legendCategorySet?.valuesArray
        },
        () => self.invalidateCases(),
        {
          name: "DataConfigurationModel.afterCreate.reaction [allCategoriesForRoles]",
          equals: comparer.structural
        }
      ))
      // respond to change of any attribute description (including via undo/redo, which
      // bypasses the setAttribute action that would otherwise call invalidateCases)
      addDisposer(self, reaction(
        () => self.attributeDescriptionsStr,
        () => {
          self.invalidateCases()
        },
        {name: "DataConfigurationModel.afterCreate.reaction [attribute descriptions]"}
      ))
      // Invalidate cases when items change in data set.
      addDisposer(self, reaction(
        () => self.dataset?.itemIds,
        () => self.invalidateCases(), {
          // note that we don't use comparer.structural here -- the entire array changes when its contents change
          name: "DataConfigurationModel.afterCreate.reaction [self.dataset?.itemIds]"
        }
      ))
      // Invalidate cases when displayOnlySelectedCases changes.
      addDisposer(self, reaction(
        () => self.displayOnlySelectedCases,
        () => self.invalidateCases(),
        { name: "DataConfigurationModel.afterCreate.reaction [displayOnlySelectedCases invalidate cache]" }
      ))
      // Invalidate cases when selection changes.
      addDisposer(self, reaction(
        () => self.dataset?.selection.values(),
        () => {
          if (self.displayOnlySelectedCases) {
            self.invalidateCases()
          } else {
            self.allCasesForCategoryAreSelected.invalidateAll()
          }
        },
        {
          name: "DataConfigurationModel.afterCreate.reaction [allCasesForCategoryAreSelected invalidate cache]",
          equals: comparer.structural
        }
      ))
      // invalidate caches when set of visible cases changes
      addDisposer(self, reaction(
        () => [self.hiddenCases.length, self.caseDataHash],
        () => self.invalidateCases(),
        { name: "DataConfigurationModel.afterCreate.reaction [hiddenCases,caseDataHash]", equals: comparer.structural }
      ))
      // invalidate filtered cases when childmost collection changes
      addDisposer(self, reaction(
        () => self.childmostCollectionIDForAxisAttributes,
        () => self._clearFilteredCases(self.dataset),
        { name: "DataConfigurationModel.afterCreate.reaction [childmost collection]" }
      ))
    },
    setDataset(dataset: IDataSet | undefined, metadata: IDataSetMetadata | undefined) {
      self.dataset = dataset
      self.metadata = metadata
      // Call the following immediately rather than as a reaction because of timing issues
      self.handleDataSetChange(dataset)
    },
    clearAttributes() {
      self._attributeDescriptions.clear()
    },
    setAttribute(role: AttrRole, desc?: IAttributeDescriptionSnapshot) {
      self._setAttributeDescription(role, desc)
      self.setPointsNeedUpdating(true)
      self.invalidateCases()
      // No harm in invalidating even if not numeric
      self.numericValuesForAttribute.invalidate(role, self.attributeType(role))
    },
    setAttributeType(role: AttrRole, type: AttributeType, plotNumber = 0) {
      self._attributeDescriptions.get(role)?.setType(type)
      self._setAttributeType(type, plotNumber)
    },
    addNewHiddenCases(hiddenCases: string[]) {
      self.hiddenCases.push(...hiddenCases)
      self.invalidateCases()
    },
    clearHiddenCases() {
      self.hiddenCases.replace([])
      self.invalidateCases()
    },
    setHiddenCases(hiddenCases: string[]) {
      self.hiddenCases.replace(hiddenCases)
      self.invalidateCases()
    },
    setDisplayOnlySelectedCases(displayOnlySelectedCases: boolean) {
      self.displayOnlySelectedCases = displayOnlySelectedCases || undefined
      self.invalidateCases()
    }
  }))
  .actions(self => ({
    removeAttributeFromRole(role: AttrRole, attrID: string) {
      self.setAttribute(role)
    },
  }))
  .views(self => ({
    onAction(handler: (actionCall: ISerializedActionCall) => void) {
      const id = uniqueId()
      self.handlers.set(id, handler)
      return () => {
        self.handlers.delete(id)
      }
    }
  }))
  // performs the specified action so that response actions are included and undo/redo strings assigned
  .actions(applyModelChange)

export interface IDataConfigurationModel extends Instance<typeof DataConfigurationModel> {
}

export interface IDataConfigurationModelSnapshot extends SnapshotIn<typeof DataConfigurationModel> {
}

export interface IDataConfigurationWithFilterFormula extends IDataConfigurationModel {
  filterFormula: IFormula
}

export function isFilterFormulaDataConfiguration(dataConfig?: IDataConfigurationModel):
  dataConfig is IDataConfigurationWithFilterFormula {
    return !!dataConfig?.hasFilterFormula
  }
